"use client";
import { useAtomValue, useAtom } from "jotai";
import dynamic from "next/dynamic";
import {
  ReactFlow,
  SelectionMode,
  ConnectionMode,
  Node,
  useReactFlow,
} from "@xyflow/react";
import { ReactNode, useRef, useMemo, useEffect } from "react";
import classNames from "classnames";
import {
  cursorAtom,
  edgesAtom,
  isViewOnlyModeAtom,
  nodesAtom,
  selectedNodeTypeAtom,
  horizontalHelperLineAtom,
  verticalHelperLineAtom,
  isViewerAtom,
  isPublicAtom,
  draftFocusNodeIdAtom,
  previewNodeIdAtom,
  nodeContextMenuAtom,
  tourIntroStepAtom,
  isTourOrExplanationRunningAtom,
} from "@zapier/canvas-state";
import {
  handlePartialJSONStream,
  zslOutlineToNodesAndEdgesAtom,
  partialOutlineAtom,
  outlineAtom,
} from "@zapier/streaming";

import isConnectingTwoZapSteps from "../../utils/isConnectingTwoZapSteps";
import { ThirdPartyIntegrations } from "../../components/ThirdPartyIntegrations";
import useRemoveTempNodesAndEdges from "../../hooks/useRemoveTempNodesAndEdges";
import { useCanvasEmbedEvents } from "../../hooks/useCanvasEmbedEvents";
import { useStartTourFromQueryParams } from "../../hooks/useStartTourFromQueryParams";
import { areElementsSelectable } from "../../utils/elementsSelectable";

import { FocusModeWrapper } from "../FocusModeWrapper";
import { TourAlert } from "../TourAlert";
import { KeydownListeners } from "../KeydownListeners";
import { ContextMenu } from "../ContextMenu";
import { TourLoadingIndicator } from "../TourLoadingIndicator";

import { DraftAssetPreview } from "./DraftAssetPreview";
import { nodeTypes } from "./nodeTypes";
import { edgeTypes } from "./edgeTypes";
import { useReactFlowCallbacks } from "./useReactFlowCallbacks";
import { useDeleteCallbacks } from "./useDeleteCallbacks";
import { HelperLines } from "./HelperLines";
import { useOnNodeClick } from "./useOnNodeClick";
import { useOnSelection } from "./useOnSelection";
import { useOnPaneClick } from "./useOnPaneClick";
import { useCopyPaste } from "./useCopyPaste";
import { useImageInsert } from "./useImageInsert";
import { DragDropOverlay } from "./DragDropOverlay";
import ErrorBoundary from "./ErrorBoundary";
import { getCursorType, getGroupNodesFirst } from "./utils";
import { TourIntroPanel } from "./nodeTypes/AnnotationNode";
import { WarningManager } from "../WarningManager";

const Sidebar = dynamic(() => import("./Sidebar").then((mod) => mod.Sidebar), {
  ssr: false,
});

type Props = {
  children: ReactNode;
};

export function Canvas({ children }: Props) {
  const [draftFocusNodeId, setDraftFocusNodeId] = useAtom(draftFocusNodeIdAtom);
  const cursorMode = useAtomValue(cursorAtom);
  const [nodes, setNodes] = useAtom(nodesAtom);
  const [edges, setEdges] = useAtom(edgesAtom);

  const ref = useRef<HTMLDivElement | null>(null);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isViewer = useAtomValue(isViewerAtom);
  const isPublic = useAtomValue(isPublicAtom);
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const hasSelectedNodeType = Boolean(selectedNodeType);
  const helperLineHorizontal = useAtomValue(horizontalHelperLineAtom);
  const helperLineVertical = useAtomValue(verticalHelperLineAtom);
  const previewNodeId = useAtomValue(previewNodeIdAtom);
  const tourIntroStep = useAtomValue(tourIntroStepAtom);
  const isTourOrExplanationRunning = useAtomValue(
    isTourOrExplanationRunningAtom,
  );
  const [nodeContextMenu, setNodeContextMenu] = useAtom(nodeContextMenuAtom);
  const { onNodeDeleteFromContextMenu } = useDeleteCallbacks();

  useCopyPaste();
  const isDraggingFiles = useImageInsert(ref);
  useStartTourFromQueryParams();

  const isDragAndDropOverlayVisible = !isViewOnlyMode && isDraggingFiles;

  const {
    onNodesChange,
    onEdgesChange,
    onNodeContextMenu,
    onPaneMouseMove,
    onInitSetViewport,
    onConnect,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onConnectStart,
    onConnectEnd,
    onReconnectStart,
    onReconnectEnd,
    onReconnect,
  } = useReactFlowCallbacks(ref);
  const { onNodeClick } = useOnNodeClick(ref);
  const { onSelectionStart, onSelectionEnd } = useOnSelection();
  const { onPaneClick } = useOnPaneClick(ref);
  const outlineData = useAtomValue(outlineAtom);
  const partialOutlineData = useAtomValue(partialOutlineAtom);
  const convertOutlineToNodesAndEdges = useAtomValue(
    zslOutlineToNodesAndEdgesAtom,
  );
  useRemoveTempNodesAndEdges();

  const hasPannedRef = useRef(false);
  const { setViewport } = useReactFlow();

  const { onNodeDeleteFromKeyboard, onEdgesDeleteFromKeyboard } =
    useDeleteCallbacks();

  useCanvasEmbedEvents();

  const handlePanelClose = () => {
    setDraftFocusNodeId(null);
  };
  // In xyflow, your group nodes have to exist in the array before their children.
  // Otherwise, you get this error:
  // Parent node `id` not found. Please make sure that parent nodes are in front of their child nodes in the nodes array.
  const nodesWithParentsFirst = useMemo(() => {
    return getGroupNodesFirst(nodes);
  }, [nodes]);

  /* This effect is used to update the canvas when the outline data is set.
   * Taken from this discussion https://github.com/pmndrs/jotai/discussions/271 as a valid solution to keep the react tree in sync with a jotai atom updated from a stream.
   */
  useEffect(() => {
    hasPannedRef.current = false;

    const parsedOutlineData = handlePartialJSONStream(outlineData);
    const parsedPartialOutlineData =
      handlePartialJSONStream(partialOutlineData);
    const isOutlineDataSet = Object.keys(parsedOutlineData).length > 0;
    const parsedData = isOutlineDataSet
      ? parsedOutlineData
      : parsedPartialOutlineData;
    const { nodes, edges } = convertOutlineToNodesAndEdges(parsedData);
    if (nodes.length > 0) {
      /* ------------------------------------------------------------
       * Give ReactFlow one tick to mount the new nodes, then pan once to
       * the first node.  This avoids stale co-ordinates and the flash.
       * ---------------------------------------------------------- */
      setTimeout(() => {
        setNodes(nodes);
        setEdges(edges);

        /* ------------------------------------------------------------
         * Pan viewport to the first streamed node (only once).
         * ---------------------------------------------------------- */
        if (!hasPannedRef.current) {
          const firstNode = nodes[0];
          if (firstNode?.position) {
            const x = -(firstNode.position.x - 150); // px from left edge
            const y = -(firstNode.position.y - 160); // px from top edge
            setViewport({ x, y, zoom: 0.9 });
            hasPannedRef.current = true;
          }
        }
      }, 50);
    }
  }, [
    partialOutlineData,
    outlineData,
    setNodes,
    setEdges,
    convertOutlineToNodesAndEdges,
    setViewport,
  ]);

  return (
    <>
      <WarningManager />
      <ErrorBoundary>
        <TourAlert />
        {previewNodeId && <FocusModeWrapper previewNodeId={previewNodeId} />}
        <ThirdPartyIntegrations />
        <KeydownListeners />
        <ReactFlow
          ref={ref}
          connectionMode={ConnectionMode.Loose}
          nodes={nodesWithParentsFirst as Node[]}
          edges={edges}
          elementsSelectable={areElementsSelectable({
            isViewOnlyMode,
            isViewer,
            isPublic,
          })}
          proOptions={{ hideAttribution: true }}
          selectionOnDrag={true}
          onNodesDelete={onNodeDeleteFromKeyboard}
          onEdgesDelete={onEdgesDeleteFromKeyboard}
          minZoom={0.25}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          panOnScroll={cursorMode !== "pan"}
          onNodeContextMenu={onNodeContextMenu}
          onPaneClick={onPaneClick}
          onPaneMouseMove={onPaneMouseMove}
          onMoveStart={() => {
            /* To prevent this bug in floating toolbar where popover menu is disconnected from the input
            when you pan: https://cdn.zappy.app/f78f666d913ec41747b8c4867e3ec1d9.gif */
            window.dispatchEvent(new Event("scroll", { bubbles: true }));
          }}
          onSelectionStart={onSelectionStart}
          onSelectionEnd={onSelectionEnd}
          onInit={onInitSetViewport}
          onNodeClick={onNodeClick}
          className={classNames(
            { "view-only-mode": isViewOnlyMode },
            {
              [`${getCursorType(cursorMode, hasSelectedNodeType)}-cursor`]:
                getCursorType(cursorMode, hasSelectedNodeType),
            },
          )}
          panOnDrag={
            cursorMode === "pan" ||
            (selectedNodeType !== null && selectedNodeType !== "group")
          }
          selectionMode={SelectionMode.Partial}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onReconnectStart={onReconnectStart}
          onReconnectEnd={onReconnectEnd}
          onReconnect={onReconnect}
          nodeDragThreshold={10}
          connectionRadius={100}
          isValidConnection={(connection) =>
            !isConnectingTwoZapSteps(connection)
          }
          deleteKeyCode={["Backspace", "Delete"]}
          snapToGrid={true}
          snapGrid={[10, 10]}
          nodesDraggable={!isViewOnlyMode}
          edgesReconnectable={!isViewOnlyMode}
        >
          {children}
          <HelperLines
            horizontal={helperLineHorizontal}
            vertical={helperLineVertical}
          />
          {nodeContextMenu && (
            <ContextMenu
              {...nodeContextMenu}
              onClose={() => setNodeContextMenu(null)}
              onNodeDelete={onNodeDeleteFromContextMenu}
            />
          )}
          {tourIntroStep && <TourIntroPanel />}
          {isTourOrExplanationRunning && <TourLoadingIndicator />}
          {isDragAndDropOverlayVisible && <DragDropOverlay />}
        </ReactFlow>
        {draftFocusNodeId && (
          <DraftAssetPreview
            onClose={handlePanelClose}
            nodeId={draftFocusNodeId}
          />
        )}
        {/* This for the React portal to attach to.
        This is so events are no longer trapped by ReactFlow */}
        <div id="toolbar-content" />
        <Sidebar />
      </ErrorBoundary>
    </>
  );
}
